import IframeRenderer, { iframeModel } from '@native-html/iframe-plugin';
import { defaultSystemFonts, RenderHTML } from 'react-native-render-html';
import { mvs } from 'react-native-size-matters';
import { WebView } from 'react-native-webview';

import { useAppSelector } from '../../app/hooks';
import { WINDOW_WIDTH } from '../../constants';
import {
  selectFontFamily,
  selectMainColor,
  selectTextAlign,
} from '../../features/appconfig/store/selectors';
import { generateFontVariantsForRenderingRawHTML } from '../../helpers/fontMapper';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';

const renderers = {
  iframe: IframeRenderer,
};

const customHTMLElementModels = {
  iframe: iframeModel,
};

interface HTMLContentRendererProps {
  content: string;
  textColor?: string;
  opacity?: number;
  maxCharacters?: number;
  textAlign?: 'left' | 'right' | 'center' | 'justify' | 'auto' | null;
  disableStrongStyling?: boolean;
}

export function HTMLContentRenderer({
  content,
  textColor = '#252525',
  opacity = 1,
  maxCharacters,
  textAlign = null,
  disableStrongStyling = false,
}: HTMLContentRendererProps) {
  const fontFamily = useAppSelector(selectFontFamily);
  const globalTextAlign = useAppSelector(selectTextAlign);
  const mainColor = useAppSelector(selectMainColor);

  // use prop textAlign if provided, otherwise fall back to global setting
  const effectiveTextAlign = textAlign ?? globalTextAlign ?? 'left';

  // font configuration
  const selectedFontVariants = generateFontVariantsForRenderingRawHTML(fontFamily);
  const systemFonts = [...defaultSystemFonts, ...selectedFontVariants];

  // dynamic font sizes
  const paragraphFontSize = useAdjustedFontSize(mvs(15));
  const paragraphLineHeight = useAdjustedFontSize(mvs(18));

  // process content based on max characters
  const processedContent =
    maxCharacters && content.length > maxCharacters
      ? content.substring(0, maxCharacters) + '...'
      : content;

  // clean html content to remove empty paragraphs and useless text
  let cleanedContent = processedContent.replace(/<p>\s*<\/p>/g, ''); // remove empty paragraphs
  // .replace(/Hébergé par Audiomeans[\s\S]*$/i, ''); -> remove content from 'Hébergé par Audiomeans' to the end

  // remove strong tags if disableStrongStyling is true
  if (disableStrongStyling) {
    cleanedContent = cleanedContent.replace(/<\/?strong>/g, '');
  }

  return (
    <RenderHTML
      WebView={WebView}
      renderers={renderers}
      customHTMLElementModels={customHTMLElementModels}
      contentWidth={WINDOW_WIDTH}
      source={{ html: cleanedContent }}
      tagsStyles={{
        p: {
          fontSize: paragraphFontSize,
          color: textColor,
          lineHeight: paragraphLineHeight,
          fontFamily: selectedFontVariants[0],
          textAlign: effectiveTextAlign,
          marginBottom: mvs(2),
          marginTop: 0,
        },
        strong: {
          fontFamily: selectedFontVariants[1],
        },
        em: {
          fontStyle: 'italic',
        },
        body: {
          fontFamily: selectedFontVariants[0],
          color: textColor,
          margin: 0,
          padding: 0,
          opacity: opacity,
        },
        h3: {
          fontSize: paragraphFontSize + 5,
          fontFamily: selectedFontVariants[1],
          color: textColor,
          marginTop: mvs(16),
          marginBottom: mvs(8),
        },
        a: {
          color: mainColor,
          textDecorationLine: 'underline',
        },
        ul: {
          paddingLeft: mvs(16),
          marginBottom: mvs(12),
        },
        li: {
          fontSize: paragraphFontSize,
          fontFamily: selectedFontVariants[0],
          marginBottom: mvs(4),
        },
        br: {
          height: mvs(6),
        },
      }}
      systemFonts={systemFonts}
    />
  );
}
